import { useEffect, useMemo, useState } from "react";

import { CheckBadgeIcon, SparklesIcon, TruckIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import useMvlSearch from "../../data/useMvl";
import { IAmazonMvl } from "../../models/mvl/IAmazonMvl";
import { IVehicle } from "../../models/Vehicle";
import classNames from "../../utils/classNames";
import { SignalState, scoreMvlRows } from "../../utils/kTypeMatch";
import { ButtonInput } from "./Inputs/ButtonInput";

interface IKTypeLookupModalProps {
  open: boolean;
  onClose: () => void;
  initialMake?: string | null;
  initialModel?: string | null;
  vehicle?: IVehicle | null;
  onSelect: (kType: number, row: IAmazonMvl) => void;
}

type VehicleInfoField = { label: string; value: string };

// Only call out a suggested KType when enough of the vehicle's data lines up.
const SUGGESTION_THRESHOLD = 60;

function signalTone(state: SignalState) {
  switch (state) {
    case "match":
      return "text-emerald-700 font-medium";
    case "near":
      return "text-amber-700";
    case "mismatch":
      return "text-gray-400 line-through decoration-gray-300";
    default:
      return "text-gray-700";
  }
}

function scoreTone(percent: number) {
  if (percent >= SUGGESTION_THRESHOLD) return "bg-emerald-100 text-emerald-800";
  if (percent >= 35) return "bg-amber-100 text-amber-800";
  return "bg-gray-100 text-gray-600";
}

function buildVehicleInfoFields(vehicle: IVehicle): VehicleInfoField[] {
  const engineSize =
    typeof vehicle.combinedEngineCapacity === "number"
      ? `${vehicle.combinedEngineCapacity}cc`
      : typeof vehicle.engineCapacity === "number"
        ? `${vehicle.engineCapacity}cc`
        : null;

  const power =
    [
      typeof vehicle.maximumPowerBHP === "number" ? `${Math.round(vehicle.maximumPowerBHP)}bhp` : null,
      typeof vehicle.maximumPowerInKW === "number" ? `${Math.round(vehicle.maximumPowerInKW)}kW` : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;

  const monthYear = (value: string | null | undefined) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? null
      : date.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
  };

  const firstRegistered = monthYear(vehicle.dateFirstRegistered);
  const built = monthYear(vehicle.manufacturerBuildDate);

  const fields: Array<{ label: string; value: string | null | undefined }> = [
    { label: "Reg", value: vehicle.vrm },
    { label: "Year", value: vehicle.dvlaYearOfManufacture?.toString() },
    { label: "Make", value: vehicle.combinedMake || vehicle.dvlaMake },
    { label: "Model", value: vehicle.combinedModel || vehicle.dvlaModel },
    { label: "Range", value: vehicle.modelRangeDescription },
    { label: "Series", value: vehicle.modelSeries },
    { label: "Variant", value: vehicle.modelVariantDescription },
    { label: "Body", value: vehicle.bodyStyleDescription || vehicle.bodyStyle },
    { label: "Fuel", value: vehicle.combinedFuelType || vehicle.fuelType || vehicle.fuelDescription },
    { label: "Engine Size", value: engineSize },
    { label: "Engine Code", value: vehicle.engineModelCode },
    { label: "Power", value: power },
    {
      label: "Gearbox",
      value: vehicle.combinedTransmission || vehicle.dvlaTransmission || vehicle.transmission,
    },
    { label: "Drive", value: [vehicle.driveType, vehicle.driveAxle].filter(Boolean).join(" · ") || null },
    {
      label: "Doors",
      value:
        typeof vehicle.numberOfDoors === "number" && vehicle.numberOfDoors > 0
          ? vehicle.numberOfDoors.toString()
          : null,
    },
    { label: "Built", value: built },
    { label: "First Reg", value: firstRegistered },
    { label: "KType", value: vehicle.kType?.toString() },
  ];

  return fields
    .filter((f): f is { label: string; value: string } => !!f.value && String(f.value).trim().length > 0)
    .map((f) => ({ label: f.label, value: String(f.value) }));
}

// Search the Master Vehicle List (via Betty) and pick a KType for a vehicle.
export default function KTypeLookupModal({
  open,
  onClose,
  initialMake,
  initialModel,
  vehicle,
  onSelect,
}: IKTypeLookupModalProps) {
  const [make, setMake] = useState(initialMake ?? "");
  const [model, setModel] = useState(initialModel ?? "");
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMake(initialMake ?? "");
    setModel(initialModel ?? "");
    setQuery("");
    setSubmitted(!!(initialMake || initialModel));
  }, [open, initialMake, initialModel]);

  const { search, results } = useMvlSearch(
    {
      make: make.trim() || undefined,
      model: model.trim() || undefined,
      query: query.trim() || undefined,
      take: 200,
    },
    open && submitted
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const vehicleFields = useMemo(
    () => (vehicle ? buildVehicleInfoFields(vehicle) : []),
    [vehicle]
  );

  // Ranked copy of the results - the search request itself is unchanged.
  const scored = useMemo(() => scoreMvlRows(results, vehicle), [results, vehicle]);
  // Rows outside the build window are never suggested, however well the rest of the row scores.
  const best = scored.find((s) => !s.match.outOfRange);
  const suggestion =
    best && best.match.percent != null && best.match.percent >= SUGGESTION_THRESHOLD ? best : null;

  const currentKType = vehicle?.kType ?? null;
  const suggestionIsCurrent = !!suggestion && currentKType != null && suggestion.row.kType === currentKType;

  return (
    <Modal open={open} setOpen={onClose} width="max-w-7xl">
      <div className="space-y-4">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Lookup KType</h2>
          <p className="mt-1 text-sm text-gray-600">
            Search the Master Vehicle List by make, model or free text, then pick a row to set the vehicle KType.
            {vehicle &&
              " Results are ranked against the vehicle on file — green values match, struck-through values don't. Rows built outside the vehicle's year range are ruled out and never suggested."}
          </p>
        </div>

        {vehicleFields.length > 0 && (
          <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2.5">
            <div className="mb-2 flex items-center gap-1.5 text-slate-600">
              <TruckIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <p className="text-[11px] font-semibold uppercase tracking-wide">Vehicle on file</p>
            </div>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 sm:grid-cols-3 md:grid-cols-4">
              {vehicleFields.map((field) => (
                <div key={field.label} className="min-w-0">
                  <dt className="text-[10px] font-medium uppercase tracking-wide text-slate-500">
                    {field.label}
                  </dt>
                  <dd className="truncate text-sm font-medium text-slate-900" title={field.value}>
                    {field.value}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        <form onSubmit={handleSearch} className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Make</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={make}
              onChange={(e) => setMake(e.target.value)}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Model</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={model}
              onChange={(e) => setModel(e.target.value)}
            />
          </label>
          <label className="text-sm sm:col-span-1">
            <span className="font-medium text-gray-500">Query</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Variant, ktype…"
            />
          </label>
          <div className="flex items-end">
            <ButtonInput
              label={search.isFetching ? "Searching…" : "Search"}
              isSubmit={true}
              onClick={undefined}
              classes={"w-full"}
              isLoading={search.isFetching}
            />
          </div>
        </form>

        {suggestion && !search.isFetching && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5">
            {suggestionIsCurrent ? (
              <CheckBadgeIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
            ) : (
              <SparklesIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-emerald-900">
                {suggestionIsCurrent
                  ? `The KType already on the vehicle (${currentKType}) matches the suggestion`
                  : `Suggested KType ${suggestion.row.kType}`}{" "}
                — {suggestion.row.make} {suggestion.row.model} {suggestion.row.variantName} (
                {suggestion.match.percent}% match)
              </p>
              <p className="mt-0.5 text-xs text-emerald-800">
                {suggestion.match.matched.join(" · ") || "Closest row to the vehicle on file."}
              </p>
              {currentKType != null && !suggestionIsCurrent && (
                <p className="mt-1 text-xs font-medium text-amber-800">
                  Vehicle is currently set to KType {currentKType}, which is not the closest match.
                </p>
              )}
            </div>
            {!suggestionIsCurrent && (
              <button
                type="button"
                disabled={!suggestion.row.kType}
                onClick={() =>
                  suggestion.row.kType != null && onSelect(suggestion.row.kType, suggestion.row)
                }
                className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-50"
              >
                Use suggestion
              </button>
            )}
          </div>
        )}

        <div className="max-h-96 overflow-auto rounded border border-gray-200">
          {search.isFetching ? (
            <p className="p-6 text-center text-sm text-gray-500">Searching…</p>
          ) : !submitted ? (
            <p className="p-6 text-center text-sm text-gray-500">Enter a make/model and search.</p>
          ) : results.length === 0 ? (
            <p className="p-6 text-center text-sm text-gray-500">No matching vehicles found.</p>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr>
                  {vehicle && (
                    <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Match</th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">KType</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Make</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Model</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Variant</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Body</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Energy</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Power</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Drive</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase text-gray-500">Years</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {scored.map(({ row, match }) => {
                  const isSuggested = suggestion?.row.id === row.id;
                  const isCurrent = currentKType != null && row.kType === currentKType;
                  const reasons = [
                    ...match.matched.map((r) => `✓ ${r}`),
                    ...match.mismatched.map((r) => `✗ ${r}`),
                  ].join("\n");

                  return (
                    <tr
                      key={row.id}
                      className={classNames(
                        isSuggested && "bg-emerald-50/70",
                        isCurrent && !isSuggested && "bg-sky-50/70",
                        match.outOfRange && "opacity-60"
                      )}
                    >
                      {vehicle && (
                        <td className="whitespace-nowrap px-3 py-2">
                          {match.outOfRange ? (
                            <span
                              title={match.outOfRangeReason ?? undefined}
                              className="inline-flex items-center rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-800"
                            >
                              Wrong years
                            </span>
                          ) : match.percent == null ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : (
                            <span
                              title={reasons || undefined}
                              className={classNames(
                                "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold",
                                scoreTone(match.percent)
                              )}
                            >
                              {isSuggested && <SparklesIcon className="h-3 w-3 shrink-0" aria-hidden />}
                              {match.percent}%
                            </span>
                          )}
                        </td>
                      )}
                      <td className="whitespace-nowrap px-3 py-2 font-semibold text-gray-900">
                        {row.kType ?? "—"}
                        {isCurrent && (
                          <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-800">
                            Current
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-gray-700">{row.make ?? "—"}</td>
                      <td className="px-3 py-2 text-gray-700">{row.model ?? "—"}</td>
                      <td className={classNames("px-3 py-2", signalTone(match.signals.engine))}>
                        {row.variantName ?? "—"}
                      </td>
                      <td className={classNames("px-3 py-2", signalTone(match.signals.body))}>
                        {row.bodyStyle ?? "—"}
                      </td>
                      <td className={classNames("px-3 py-2", signalTone(match.signals.fuel))}>
                        {row.energy ?? "—"}
                      </td>
                      <td
                        className={classNames(
                          "whitespace-nowrap px-3 py-2",
                          signalTone(match.signals.power)
                        )}
                      >
                        {[row.engineOutputHp && `${row.engineOutputHp}hp`, row.engineOutputKw && `${row.engineOutputKw}kW`]
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </td>
                      <td className={classNames("px-3 py-2", signalTone(match.signals.drive))}>
                        {row.driveType ?? "—"}
                      </td>
                      <td
                        className={classNames(
                          "whitespace-nowrap px-3 py-2",
                          match.outOfRange ? "font-medium text-rose-700" : signalTone(match.signals.years)
                        )}
                        title={match.outOfRangeReason ?? undefined}
                      >
                        {[row.productStartMonthYear, row.productEndMonthYear].filter(Boolean).join(" – ") || "—"}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          type="button"
                          disabled={!row.kType}
                          onClick={() => row.kType != null && onSelect(row.kType, row)}
                          className="rounded-md bg-primary-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-primary-500 disabled:opacity-50"
                        >
                          Use
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="flex justify-end">
          <ButtonInput
            label={"Close"}
            isSubmit={false}
            onClick={onClose}
            classes={"!bg-gray-200 hover:!bg-gray-300 !text-gray-700"}
          />
        </div>
      </div>
    </Modal>
  );
}
